import PropTypes from "prop-types";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "./CustomTooltip.scss";
import { useCallback, useEffect, useId, useRef, useState } from "react";

const getAlignedPlacement = (placement) =>
  (placement ?? "top").split("-")[0];

const getTooltipMaxWidth = (scopeElement, placement) => {
  const bounds = scopeElement?.firstElementChild?.getBoundingClientRect();
  if (!bounds?.width) {
    return undefined;
  }

  const side = getAlignedPlacement(placement);
  if (side !== "top" && side !== "bottom") {
    return undefined;
  }

  const center = bounds.left + bounds.width / 2;
  const availableWidth =
    2 * (Math.min(center, window.innerWidth - center) - 8);
  return `${Math.max(0, Math.min(320, availableWidth))}px`;
};

const resolveTooltipContainer = (scopeElement, classString) => {
  const parentElement = scopeElement.parentElement;

  if (typeof classString === "string") {
    return parentElement?.closest(classString) || parentElement || scopeElement;
  }

  const container = Array.isArray(classString)
    ? classString.find((item) => item?.nodeType === 1)
    : classString?.current || classString;

  return container?.nodeType === 1
    ? container
    : parentElement || scopeElement;
};

const CustomTooltip = ({
  text,
  children,
  classString,
  placement,
  disabled = 0,
  tooltipClassName = "",
  hideOnScroll = false,
}) => {
  const tooltipId = useId();
  const [isScrolling, setIsScrolling] = useState(false);
  const [tooltipContainer, setTooltipContainer] = useState(null);
  const [tooltipPlacement, setTooltipPlacement] = useState(placement ?? "top");
  const [tooltipMaxWidth, setTooltipMaxWidth] = useState(undefined);
  const tooltipScopeRef = useRef(null);
  const updateTooltipPlacement = useCallback(() => {
    const nextPlacement = getAlignedPlacement(placement);
    setTooltipPlacement(nextPlacement);
    setTooltipMaxWidth(getTooltipMaxWidth(tooltipScopeRef.current, placement));
  }, [placement]);
  const setTooltipScope = useCallback(
    (scopeElement) => {
      if (scopeElement) {
        tooltipScopeRef.current = scopeElement;
        setTooltipContainer(
          resolveTooltipContainer(scopeElement, classString),
        );
        setTooltipPlacement(getAlignedPlacement(placement));
        setTooltipMaxWidth(getTooltipMaxWidth(scopeElement, placement));
      }
    },
    [classString, placement],
  );

  useEffect(() => {
    window.addEventListener("resize", updateTooltipPlacement);

    return () => {
      window.removeEventListener("resize", updateTooltipPlacement);
    };
  }, [updateTooltipPlacement]);

  useEffect(() => {
    if (!hideOnScroll) {
      return undefined;
    }

    let timeout;

    const handleScroll = () => {
      setIsScrolling(true);

      clearTimeout(timeout);

      timeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [hideOnScroll]);

  const renderTooltip = () => (
    <Tooltip
      id={tooltipId}
      className={`custom-tooltip-black ${tooltipClassName}`}
      style={{
        display: disabled || isScrolling ? "none" : "block",
        "--custom-tooltip-max-width": tooltipMaxWidth,
      }}
    >
      {text}
    </Tooltip>
  );

  return (
    <span
      ref={setTooltipScope}
      className="custom-tooltip-scope"
      style={{ display: "contents" }}
      onMouseEnter={updateTooltipPlacement}
      onFocusCapture={updateTooltipPlacement}
    >
      <OverlayTrigger
        trigger={["hover", "focus"]}
        placement={tooltipPlacement}
        overlay={renderTooltip()}
        container={tooltipContainer}
        flip
        popperConfig={{
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 4,
                rootBoundary: "viewport",
                mainAxis: true,
              },
            },
            {
              name: "flip",
              options: {
                boundary: "viewport",
                padding: 4,
                rootBoundary: "viewport",
                flipVariations: false,
              },
            },
          ],
        }}
        show={disabled || isScrolling ? false : undefined}
      >
        {children}
      </OverlayTrigger>
    </span>
  );
};

CustomTooltip.propTypes = {
  text: PropTypes.string,
  children: PropTypes.node,
  classString: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  placement: PropTypes.string,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  tooltipClassName: PropTypes.string,
  hideOnScroll: PropTypes.bool,
};

export default CustomTooltip;
